/**
 * Impressum data — German legal notice (§ 5 DDG / § 18 Abs. 2 MStV).
 *
 * HOW TO UPDATE:
 *   Edit `impressumData` below. All fields are used by two consumers:
 *     - SiteFooter  (minimal: publication name, email, copyright, link to /impressum)
 *     - ImpressumPage (full legal page)
 *
 * FIELDS:
 *   publicationName      — Name of the publication shown in footer and page header
 *   responsiblePerson    — Natural person responsible under press law
 *   email                — Contact e-mail address (used as mailto: href)
 *   emailResponseNote    — Optional italic note shown below the email (e.g. response time)
 *   address              — Multi-line postal address; each entry becomes its own line
 *   copyrightYear        — Year shown in the © notice in the footer (update annually)
 *   infoDate             — "Stand der Information" value shown at the bottom of the page
 *
 *   sectionHeadings      — H2 headings for the three statutory sections
 *     .anbieter            § 5 DDG provider identification heading
 *     .presse              Press liability heading
 *     .disclaimer          Disclaimer section heading
 *
 *   presseLiabilityIntro — Introductory sentence for the Pressehaftung section
 *
 *   disclaimers          — Three subsections under "Disclaimer (Haftungsausschluss)"
 *     .contentLiability    Haftung für Inhalte
 *     .linkLiability       Haftung für Links
 *     .copyright           Urheberrecht
 *
 * Each DisclaimerSection has:
 *   heading  — H3 heading
 *   body     — Full paragraph text
 */

export interface DisclaimerSection {
  heading: string
  body: string
}

export interface ImpressumData {
  publicationName: string
  responsiblePerson: string
  email: string
  emailResponseNote: string
  address: string[]
  copyrightYear: string
  infoDate: string

  sectionHeadings: {
    anbieter: string
    presse: string
    disclaimer: string
  }

  presseLiabilityIntro: string

  disclaimers: {
    contentLiability: DisclaimerSection
    linkLiability: DisclaimerSection
    copyright: DisclaimerSection
  }
}

export const impressumData: ImpressumData = {
  publicationName: 'Radiri News',
  responsiblePerson: 'Johannes Goller',
  email: 'info@radiri.news',
  emailResponseNote: 'Antwortzeit: Wir antworten innerhalb von 24 Stunden.',
  address: [
    'c/o MDC#info-radiri',
    'Welserstr. 3',
    '87463 Dietmannsried',
  ],
  copyrightYear: '2025',
  infoDate: 'April 2026',

  sectionHeadings: {
    anbieter: 'Anbieterkennzeichnung (gemäß § 5 DDG)',
    presse: 'Pressehaftung',
    disclaimer: 'Disclaimer (Haftungsausschluss)',
  },

  presseLiabilityIntro:
    'Inhaltlich Verantwortlich gemäß § 18 Abs. 2 MStV:',

  disclaimers: {
    contentLiability: {
      heading: 'Haftung für Inhalte',
      body: 'Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Als Diensteanbieter sind wir gemäß § 7 Abs.\u202f1 DDG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 DDG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.',
    },
    linkLiability: {
      heading: 'Haftung für Links',
      body: 'Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.',
    },
    copyright: {
      heading: 'Urheberrecht',
      body: 'Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers. Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet. Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden die Urheberrechte Dritter beachtet. Insbesondere werden Inhalte Dritter als solche gekennzeichnet. Sollten Sie trotzdem auf eine Urheberrechtsverletzung aufmerksam werden, bitten wir um einen entsprechenden Hinweis. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Inhalte umgehend entfernen.',
    },
  },
}
